// src/shared/clock-rules.js
//
// v129.148 — THE OPEN-SHIFT CEILING. Shared so the till and the Worker's
// nightly canary can never disagree about what "still working" means.
//
// THE DEFECT THIS EXISTS FOR (verified on prod, 15 Aug 2026):
// mergeCarriedOpenShifts (src/clock/state.js) already carries a still-open
// shift across the 3am business-day roll so a late finisher can clock out
// normally. It was gated on a WALL-CLOCK HOUR — `if (nowHour >= 6) return` —
// which is a proxy for "plausibly still on the floor".
//
// The proxy is wrong for the shift that matters. Overnight restocking runs
// 22:00 to 07:00, so the carry switched itself off a full hour BEFORE the crew
// finished. Measured failure rate: 26 of 26 across four stocktake nights
// (16 Jun, 24 Jun, 30 Jun, 28 Jul) and 9 stores — not one person managed to
// clock out. The control case is the same night at the same store: a 00:16
// clock-out worked, because 00:16 fell inside the window.
//
// The honest question is not "what time is it" but "how long has this shift
// been running". A 22:00 start at 07:00 is nine hours old and real. The same
// row at 14:00 is sixteen hours old and is somebody who forgot.
//
// Christmas turns the monthly stocktake into nightly overnight trading across
// 28 stores, which is why the proxy had to go now.

// No shift runs longer than this (Matt, 15 Aug 2026). Past it, a row is not
// somebody still working — it is somebody who never clocked out, and it belongs
// to the forgot-to-clock-out banner (which lets a manager enter the real
// finishing time) and to the nightly canary, not to a till offering a stale
// "Clock Out" that would record hours nobody worked.
export const MAX_OPEN_SHIFT_HOURS = 12;

// v136.9 (17 Oct 2026, Matt) - THE 12 HOURS ARE 12 REAL HOURS. clock_in is the STORE'S wall time (the till stamps
// 'YYYY-MM-DDTHH:MM:00' from its own clock) kept in a timestamptz column, so the database reads it as if it were UTC
// and hands it back as '...+00:00'. This used to Date.parse that and compare it with Date.now(), the real instant, so
// every age read 4 hours short in the UAE (UTC+4) and 3 hours short in Qatar (UTC+3): the "12 hour" limit was really
// 16 hours (UAE) or 15 hours (Qatar). Now the wall-time digits are read as the store's own time (any suffix is
// ignored: the digits ARE the wall time) and turned into the real instant with the store's offset. Neither country
// has daylight saving, so a fixed offset is exact. Matt accepted that stale shifts now close sooner.
export const UAE_UTC_OFFSET_HOURS = 4;
export const QATAR_UTC_OFFSET_HOURS = 3;

// Is this a Qatar country / region label ("ELC - Qatar", "Qatar")? Everything else is the UAE.
export function isQatarCountry(label) {
  return /qatar/i.test(String(label == null ? '' : label));
}
export function utcOffsetHoursFor(isQatar) {
  return isQatar ? QATAR_UTC_OFFSET_HOURS : UAE_UTC_OFFSET_HOURS;
}

// The wall-time digits of a stored clock time as a UTC millisecond count (no timezone applied), or NaN.
export function wallTimeMs(ts) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(String(ts == null ? '' : ts).trim());
  if (!m) return NaN;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
}

// How long an open shift has been running, in REAL hours, from its clock_in.
// utcOffsetHours: the store's offset from UTC (4 for the UAE, 3 for Qatar; utcOffsetHoursFor). When it is not given
// the UAE's 4 is used: most stores, and for a Qatar row it errs an hour towards closing sooner, never later.
//
// Returns null when there is no parseable clock_in. Callers MUST treat null as
// "not eligible" rather than defaulting to zero: this decides whether a till
// writes to somebody's attendance record, and guessing there is how you record
// hours nobody worked.
export function openShiftAgeHours(row, nowMs, utcOffsetHours) {
  if (!row || !row.clock_in) return null;
  const wall = wallTimeMs(row.clock_in);
  if (isNaN(wall)) return null;
  const off = (typeof utcOffsetHours === 'number' && isFinite(utcOffsetHours)) ? utcOffsetHours : UAE_UTC_OFFSET_HOURS;
  const started = wall - off * 3600000;
  const now = (typeof nowMs === 'number' && !isNaN(nowMs)) ? nowMs : Date.now();
  return (now - started) / 3600000;
}

// Is this open row still close enough to now to be carried onto a till?
// Negative ages (a clock_in in the future — clock skew, bad data) are refused
// rather than treated as fresh.
export function isCarryableOpenShift(row, nowMs, utcOffsetHours) {
  const age = openShiftAgeHours(row, nowMs, utcOffsetHours);
  return age !== null && age >= 0 && age <= MAX_OPEN_SHIFT_HOURS;
}

// v136.8 (16 Oct 2026 audit) - WHICH DAY A CLOCK-OUT FALLS ON. The one rule the till already uses (doClock's
// tsFor, src/clock/action.js) and the stuck-shift modal uses (submitStuckResolve, src/clock/stuck.js): a
// clock-out EARLIER than the clock-in is on the next calendar day; otherwise it is on the start day. The admin
// amend screen now uses it too (it refused a 07:00 finish for a 22:00 start). The Worker's 24-hour check
// (src/worker/clock-guard.js clockRangeError) stays the safety net behind all three.
//
// startDate 'YYYY-MM-DD'; clockIn / clockOut 'HH:MM'. Returns 'YYYY-MM-DD', or null for an unusable startDate.
// Date arithmetic only, at UTC noon: no timezone drift.
export function clockOutDate(startDate, clockIn, clockOut) {
  const ymd = String(startDate == null ? '' : startDate).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  if (!clockIn || !clockOut || !(String(clockOut) < String(clockIn))) return ymd;
  const d = new Date(ymd + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
